// What a person can ask the app to do, as one closed union of values.
//
// A command says what was asked in the app's own terms (move to the next
// unreviewed change, mark what is selected reviewed, go to the overview).
// It names no key and no element: the keyboard reaches a command through
// the bindings table (keys/bindings.ts), a button dispatches the same
// value, and each view's `update` decides what the command means there.
//
// Motions and verbs are composed with objects, never listed per pair: a
// motion is a direction and an object, and a verb acts on the selection,
// or on the object under the cursor when nothing is selected.

/** What motions move by, selections take and verbs act on. `group` is a list's own grouping (the dashboard's stacks and sections). */
export const OBJECTS = ['line', 'change', 'scope', 'file', 'draft', 'thread', 'pr', 'group'] as const;
export type Obj = (typeof OBJECTS)[number];

export const DIRS = [1, -1] as const;
/** Forward (1) or back (-1). */
export type Dir = (typeof DIRS)[number];

export const ONLYS = ['unreviewed', 'unresolved'] as const;
/** A motion that stops only at objects still waiting for the reviewer. */
export type Only = (typeof ONLYS)[number];

/** What "still waiting" means for each object; null where it means nothing. */
export const WAITING: Readonly<Record<Obj, Only | null>> = {
  line: 'unreviewed',
  change: 'unreviewed',
  scope: 'unreviewed',
  file: 'unreviewed',
  draft: null,
  thread: 'unresolved',
  pr: null,
  group: null,
};

export const VERBS = ['review', 'unmark', 'comment', 'edit', 'copy', 'discard', 'include', 'open'] as const;
export type Verb = (typeof VERBS)[number];

/**
 * The pages one pivot away from any page, the app's to go to: the
 * repository's page (`repository`), its Branches tab (`branches`), the
 * repository index (`repositories`), and the history of the subject shown
 * (`history`: a pull request's or a compare's head branch's, or a
 * history's own commits).
 */
export const NAV_PLACES = ['repository', 'branches', 'repositories', 'history'] as const;
export type NavPlace = (typeof NAV_PLACES)[number];
export const isNavPlace = (place: string): place is NavPlace => (NAV_PLACES as readonly string[]).includes(place);

export const PLACES = ['overview', 'files', 'commits', 'review', 'top', 'end', 'named-file', ...NAV_PLACES] as const;
/** Somewhere to go: a view of the pull request, an end of what is shown, the file the selected text names, or a page one pivot away (`NAV_PLACES`). */
export type Place = (typeof PLACES)[number];

export const FOLDS = ['toggle', 'open', 'closed'] as const;
export type FoldTo = (typeof FOLDS)[number];

export const PRESENTATIONS = ['centre', 'preview', 'file-list', 'wrap', 'layout', 'algorithm', 'minimap'] as const;
/** How what is shown is presented: nothing here changes the review. */
export type Presentation = (typeof PRESENTATIONS)[number];

export const MENUS = ['finder', 'stack', 'guide', 'feedback', 'settings', 'jump', 'base', 'head'] as const;
/** A menu, a picker, or a page of the app's own (`settings`). `finder` is the view's own: the file picker in Files, the filter bar on the dashboard. `jump` is the app's: the jump bar, to anywhere. `base` and `head` are a compare's header pickers (a history's ref is its `base`). */
export type Menu = (typeof MENUS)[number];

export const SCROLL_UNITS = ['line', 'half-page'] as const;
export const AXES = ['x', 'y'] as const;

/** One object, named outright: what a click on it acts on, where a key acts on the cursor's. */
export interface Ref {
  object: Obj;
  id: string;
}

export type Command =
  /** To the `count`th next (or previous) object; with `only`, passing those not waiting. */
  | { kind: 'move'; object: Obj; dir: Dir; only?: Only; count?: number }
  | { kind: 'scroll'; axis: (typeof AXES)[number]; dir: Dir; by: (typeof SCROLL_UNITS)[number]; count?: number }
  /** Select the object around the cursor (or the one named by `at`). */
  | { kind: 'select'; object: Obj; at?: Ref }
  /** Start a line range at the cursor, or clear the one there is. */
  | { kind: 'range' }
  /** Grow the selection to what encloses it (1), or shrink it back (-1). */
  | { kind: 'grow'; dir: Dir; count?: number }
  /** A verb on the selection, else on the object under the cursor (or on `at`). `advance`: then move on to the next waiting object. */
  | { kind: 'act'; verb: Verb; advance?: boolean; at?: Ref }
  | { kind: 'goto'; place: Place }
  /** Fold the foldable thing at the cursor (or `at`): `object` says which kind when a view has several, `level` every one at its depth. */
  | { kind: 'fold'; to: FoldTo; object?: Obj; level?: boolean; at?: Ref }
  | { kind: 'present'; what: Presentation }
  | { kind: 'open'; menu: Menu }
  | { kind: 'refresh' }
  /** Send what is being written (a comment box, the review). */
  | { kind: 'submit' }
  /** Leave what is open or selected. */
  | { kind: 'cancel' };

export type CommandKind = Command['kind'];

/** Commands a count multiplies. */
const COUNTED: ReadonlySet<CommandKind> = new Set(['move', 'scroll', 'grow']);

/** `command` carried out `count` times, where a count means anything to it; unchanged otherwise. */
export function withCount(command: Command, count: number | null): Command {
  if (count === null || !COUNTED.has(command.kind)) return command;
  return { ...command, count } as Command;
}

/** Whether a count means anything to commands of this kind. */
export const counted = (kind: CommandKind) => COUNTED.has(kind);

/** How many times a counted command runs. */
export const times = (command: { count?: number }) => Math.max(1, command.count ?? 1);

// ---------------------------------------------------------------- checking a value

type Field = { one: readonly unknown[]; optional?: true } | { is: 'count' | 'ref' | 'flag'; optional: true };

/** The fields of each kind of command and what each may hold: every field of the union is named here, or this does not compile. */
const SHAPES: { [K in CommandKind]: Record<Exclude<keyof Extract<Command, { kind: K }>, 'kind'>, Field> } = {
  move: { object: { one: OBJECTS }, dir: { one: DIRS }, only: { one: ONLYS, optional: true }, count: { is: 'count', optional: true } },
  scroll: { axis: { one: AXES }, dir: { one: DIRS }, by: { one: SCROLL_UNITS }, count: { is: 'count', optional: true } },
  select: { object: { one: OBJECTS }, at: { is: 'ref', optional: true } },
  range: {},
  grow: { dir: { one: DIRS }, count: { is: 'count', optional: true } },
  act: { verb: { one: VERBS }, advance: { is: 'flag', optional: true }, at: { is: 'ref', optional: true } },
  goto: { place: { one: PLACES } },
  fold: { to: { one: FOLDS }, object: { one: OBJECTS, optional: true }, level: { is: 'flag', optional: true }, at: { is: 'ref', optional: true } },
  present: { what: { one: PRESENTATIONS } },
  open: { menu: { one: MENUS } },
  refresh: {},
  submit: {},
  cancel: {},
};

const isRef = (v: unknown): v is Ref =>
  typeof v === 'object' && v !== null && (OBJECTS as readonly unknown[]).includes((v as Ref).object) && typeof (v as Ref).id === 'string';

/**
 * Why `value` is not a command, or null when it is one: for values that
 * come from outside the program (a table of remapped keys).
 */
export function commandFault(value: unknown): string | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return 'a command is an object with a kind';
  const v = value as Record<string, unknown>;
  const kind = v.kind;
  if (typeof kind !== 'string' || !Object.hasOwn(SHAPES, kind)) return `no command of kind ${JSON.stringify(kind)}`;
  const shape = SHAPES[kind as CommandKind] as Record<string, Field>;
  for (const key of Object.keys(v)) if (key !== 'kind' && !Object.hasOwn(shape, key)) return `${kind} has no ${key}`;
  for (const [name, field] of Object.entries(shape)) {
    const x = v[name];
    if (x === undefined) {
      if (field.optional) continue;
      return `${kind} needs ${name}`;
    }
    if ('one' in field) {
      if (!field.one.includes(x)) return `${kind}.${name} cannot be ${JSON.stringify(x)}`;
    } else if (field.is === 'count') {
      if (!Number.isInteger(x) || (x as number) < 1) return `${kind}.${name} is a whole number from 1`;
    } else if (field.is === 'flag') {
      if (typeof x !== 'boolean') return `${kind}.${name} is true or false`;
    } else if (!isRef(x)) return `${kind}.${name} names an object and its ID`;
  }
  if (kind === 'move' && v.only !== undefined && WAITING[v.object as Obj] !== v.only) return `move.only cannot be ${JSON.stringify(v.only)} for ${String(v.object)}`;
  return null;
}

/** Whether two commands ask for the same thing (field order aside). */
export function sameCommand(a: Command, b: Command): boolean {
  const ka = Object.keys(a).filter((k) => (a as Record<string, unknown>)[k] !== undefined);
  const kb = Object.keys(b).filter((k) => (b as Record<string, unknown>)[k] !== undefined);
  if (ka.length !== kb.length) return false;
  return ka.every((k) => {
    const x = (a as Record<string, unknown>)[k];
    const y = (b as Record<string, unknown>)[k];
    return isRef(x) && isRef(y) ? x.object === y.object && x.id === y.id : x === y;
  });
}
